/** PNBIL standard DPIA — Part A (screening) and Part B (full DPIA). Two main forms with sub-sections inside. */

import {
  DPIA_PART_A_MAIN_CODE,
  DPIA_PART_B_MAIN_CODE,
  legacyCodesForDpiaPart,
  sectionsForDpiaPart,
} from './dpiaPartSections';

export type DpiaPartKey = 'a' | 'b';

export const DPIA_PART_A_CODES = legacyCodesForDpiaPart('a');
export const DPIA_PART_B_CODES = legacyCodesForDpiaPart('b');

export const STANDARD_PNBIL_DPIA_CODES = [
  DPIA_PART_A_MAIN_CODE,
  DPIA_PART_B_MAIN_CODE,
  ...DPIA_PART_A_CODES,
  ...DPIA_PART_B_CODES,
] as const;

export type DpiaPartMeta = {
  key: DpiaPartKey;
  /** Code shown on the two main hub cards (PNBIL-D01 / PNBIL-D02). */
  hubDisplayCode: string;
  displayCode: string;
  title: string;
  footerDescription: string;
  simpleSummary: string;
  sectionCodes: readonly string[];
};

export const DPIA_PARTS: DpiaPartMeta[] = [
  {
    key: 'a',
    hubDisplayCode: 'PNBIL-D01',
    displayCode: DPIA_PART_A_MAIN_CODE,
    title: 'Part A: DPIA Screening Assessment',
    footerDescription:
      'This part of the DPIA process helps to identify whether a full DPIA is required.',
    simpleSummary: 'Does this project involve personal data, and is there any privacy risk?',
    sectionCodes: [DPIA_PART_A_MAIN_CODE, ...DPIA_PART_A_CODES],
  },
  {
    key: 'b',
    hubDisplayCode: 'PNBIL-D02',
    displayCode: DPIA_PART_B_MAIN_CODE,
    title: 'Part B: Data Protection Impact Assessment (DPIA)',
    footerDescription:
      'This part of the DPIA process is the full Data Protection Impact Assessment. It will be read together with sections A1 and A2 of the Screening Assessment (Part A of the DPIA process).',
    simpleSummary: "Let's analyze all privacy risks and define controls before proceeding.",
    sectionCodes: [DPIA_PART_B_MAIN_CODE, ...DPIA_PART_B_CODES],
  },
];

export const DPIA_INTRO_LEAD =
  'This document provides a Data Protection Impact Assessment (DPIA) screening assessment at Part A and a full DPIA template at Part B.';

/** Single My Tray label — Part A + Part B are one DPIA filling workflow per vendor. */
export const DPIA_UNIFIED_TRAY_TITLE = 'DPIA Filling';

export const DPIA_INTRO_PARAGRAPHS = [
  'You must complete Part A when planning any new process, project or initiative that involves processing Personal Data that may result in a risk to rights and freedoms of the individuals whose Personal Data is being used.',
  'Once completed, Part A must be submitted to the Data Protection Officer. If a full DPIA is required, you will be asked to complete or provide input to the full DPIA at Part B.',
  'For further guidance on completing a DPIA please refer to PNBIL\u2019s DPIA procedure.',
  'Please note, that the definitions for any undefined terms in this document can be found in PNBIL\u2019s Data Protection Policy and are applicable to this document.',
  'The Business Owner/Vendor RM completes the DPIA screening (Part A), and the DPO reviews it. If required, a full DPIA (Part B) is completed before onboarding can proceed.',
] as const;

export const DPIA_DPO_REVIEW_SUMMARY =
  'Is the risk significant enough to need a detailed assessment?';

export type DpiaPartRow = {
  code: string;
  title: string;
  applicability: 'applicable' | 'not_applicable' | null;
  item: {
    status?: string;
    needs_more_info_comment?: string | null;
    updated_at?: string | null;
    approver_feedback?: unknown;
    configured_approver_ids?: number[];
    approvers?: unknown;
  } | null;
};

export function isDpiaPartKey(raw: string | null | undefined): raw is DpiaPartKey {
  return raw === 'a' || raw === 'b';
}

export function getDpiaPartMeta(part: DpiaPartKey): DpiaPartMeta {
  return DPIA_PARTS.find((p) => p.key === part) ?? DPIA_PARTS[0];
}

export function getDpiaPartForCode(code: string): DpiaPartKey | null {
  const normalized = String(code ?? '').trim().toUpperCase();
  if (normalized === DPIA_PART_A_MAIN_CODE || (DPIA_PART_A_CODES as readonly string[]).includes(normalized)) {
    return 'a';
  }
  if (normalized === DPIA_PART_B_MAIN_CODE || (DPIA_PART_B_CODES as readonly string[]).includes(normalized)) {
    return 'b';
  }
  return null;
}

export function isStandardPnbilDpiaCode(code: string): boolean {
  return (STANDARD_PNBIL_DPIA_CODES as readonly string[]).includes(String(code ?? '').trim().toUpperCase());
}

export function rowsForDpiaPart<T extends { code: string }>(rows: T[], part: DpiaPartKey): T[] {
  const codes = new Set(getDpiaPartMeta(part).sectionCodes);
  return rows.filter((r) => codes.has(String(r.code).trim().toUpperCase()));
}

/** Section card rows for part hub (images 3/4) — one card per sub-section inside the part. */
export function sectionCardsForDpiaPart<T extends DpiaPartRow>(
  part: DpiaPartKey,
  rows: T[]
): Array<T & { sectionKey: string; displayCode: string }> {
  const byCode = new Map(rows.map((r) => [String(r.code).trim().toUpperCase(), r]));
  const mainCode = part === 'a' ? DPIA_PART_A_MAIN_CODE : DPIA_PART_B_MAIN_CODE;
  const mainRow = byCode.get(mainCode);

  return sectionsForDpiaPart(part).map((section) => {
    const legacy = byCode.get(section.legacyCode);
    const base: DpiaPartRow =
      legacy ??
      mainRow ??
      ({
        code: section.legacyCode,
        title: section.title,
        applicability: null,
        item: null,
      } as DpiaPartRow);
    return {
      ...base,
      code: section.legacyCode,
      title: section.title,
      sectionKey: section.sectionKey,
      displayCode: section.displayCode,
    } as T & { sectionKey: string; displayCode: string };
  });
}

/** Part fill is blocked only when every section in the part is explicitly not applicable. */
export function isDpiaPartExplicitlyNotApplicable(
  rows: Array<{ code: string; applicability: 'applicable' | 'not_applicable' | null }>,
  part: DpiaPartKey
): boolean {
  return derivePartApplicability(rowsForDpiaPart(rows, part)) === 'not_applicable';
}

/** Part-level applicability when every section in the part shares the same value. */
export function derivePartApplicability(
  rows: Array<{ applicability: 'applicable' | 'not_applicable' | null }>
): 'applicable' | 'not_applicable' | null {
  if (rows.length === 0) return null;
  const values = rows.map((r) => r.applicability);
  if (values.every((v) => v === 'not_applicable')) return 'not_applicable';
  if (values.every((v) => v === 'applicable')) return 'applicable';
  if (values.some((v) => v === 'applicable')) return 'applicable';
  if (values.every((v) => v === 'not_applicable' || v === 'applicable')) {
    return values.includes('applicable') ? 'applicable' : 'not_applicable';
  }
  return null;
}

const STATUS_PRIORITY = [
  'needs_more_information',
  'pending_approval',
  'draft',
  'approved',
] as const;

/** Aggregate section statuses for a part card header badge. */
export function derivePartAggregateStatus(
  rows: Array<{ applicability: 'applicable' | 'not_applicable' | null; item: { status?: string } | null }>
): string {
  const applicable = rows.filter((r) => r.applicability === 'applicable');
  if (applicable.length === 0) {
    if (rows.length > 0 && rows.every((r) => r.applicability === 'not_applicable')) {
      return 'not_applicable';
    }
    return 'draft';
  }
  for (const st of STATUS_PRIORITY) {
    if (applicable.some((r) => (r.item?.status ?? 'draft') === st)) return st;
  }
  return 'draft';
}

export function latestPartUpdatedAt(
  rows: Array<{ item: { updated_at?: string | null } | null }>
): string | null {
  let latest: string | null = null;
  for (const r of rows) {
    const iso = r.item?.updated_at;
    if (!iso) continue;
    if (!latest || iso > latest) latest = iso;
  }
  return latest;
}

export function groupDpiaManagementRows<T extends { code: string }>(
  rows: T[]
): { part: DpiaPartMeta; rows: T[] }[] {
  return DPIA_PARTS.map((part) => ({
    part,
    rows: rows.filter((r) => (part.sectionCodes as readonly string[]).includes(r.code)),
  })).filter((g) => g.rows.length > 0);
}
